/**
 * @file This module provides custom theme configuration for the application using the MUI theme creation API.
 * It sets the primary and secondary color palettes, various custom colors, typography settings, and component style overrides.
 * The `EBABtheme` is the final theme which is a fusion of the `BrandTheme` and additional component style overrides.
 * It can be used in the application with MUI's ThemeProvider to consistently apply the styles across the app.
 */
import { createTheme } from '@mui/material/styles';

/**
 * The `BrandTheme` object defines the foundational color palette and typography settings for the application.
 * It uses MUI's createTheme function to create a theme configuration.
 *
 * @constant
 * @type {Object}
 */
const BrandTheme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: '#6c6aa0'
        },
        secondary: {
            main: '#353d8f'
        },
        background: {
            default: '#F7F7FA',
            panel: '#FFFFFF',
            header: '#313131',
            modalGradientBlue:
                'linear-gradient(180deg, #87CBE1 4.53%, #6C717D 99.99%, #6D7581 100%)',
            score_ellipse: '#D2E4F5'
        },
        abbey: {
            lightest: '#6F757C'
        },
        kimberley: {
            main: '#696E9F',
            lighter: '#888CB3',
            darker: '#535782',
            darkest: '#494D72'
        },
        chathamsBlue: {
            lighter: '#2656B2'
        },
        grey: {
            dark: '#808080'
        },
        orange: {
            main: '#E4922C',
            darker: '#D88621'
        },
        filters: {
            unselected: '#FFFFFF',
            selected: '#EAE7F1',
            iconSet: '#ABABCA',
            iconUnset: '#E9EAF1'
        },
        disabled: {
            main: '#F0F0F0',
            darker: '#CACACA',
            darkest: '#7F7E85'
        },
        font: {
            white: '#FFFFFF',
            link: '#3267AD',
            light: '#63686F'
        },
        rankingTable: {
            scrollBar: '#ABABCA',
            border: '#6F757C',
            hidden: '#B0AEB4',
            faded: '#D6D6D6',
            selected: '#F6F6F6',
            borderWithOpacity: '#C1C1C1'
        },
        quartileBars: [
            { unselected: '#94BFE7', selected: '#494D72' },
            { unselected: '#A9CBEC', selected: '#535782' },
            { unselected: '#BED8F0', selected: '#5D6291' },
            { unselected: '#D2E4F5', selected: '#696E9F' }
        ],
        scatterPlot: {
            dotColors: ['#B0AEB4', '#569AD9', '#465B7D', '#D2637C'],
            background: ['#D8D7E6', '#C8C8DC', '#ABABCA', '#8C8CB6', '#7778A6'],
            frontierPoints: {
                stroke: {
                    initial: '#FFFFFF',
                    hover: '#94BFE7'
                }
            },
            skillsPoints: {
                stroke: {
                    initial: '#FFFFFF',
                    hover: '#94BFE7'
                }
            },
            skillDot: '#102349',
            barColors: {
                'Stock picking': '#696974',
                Sizing: '#6D9AB7',
                'Entry timing': '#78978F',
                'Scaling in': '#A6D9C6',
                'Size adjusting': '#8CD9F1',
                'Scaling out': '#B6C195',
                'Exit timing': '#F3E7B3'
            }
        },
        tooltip: {
            background: '#F0F0F0',
            divider: '#CACACA'
        },
        errorText: '#d32f2f',
        zeroLine: {
            stroke: '#000000'
        },
        overTimeChart: {
            showcaseCyan: '#4DA9B1',
            gridStroke: '#DCD4D0',
            median: '#B0AEB4',
            medianGreyed: '#D3D2D6'
        },
        pricingCheckBoxTick: '#748C9B'
    },
    sizing: {
        scatterPlot: {
            width: 500,
            height: 500,
            margin: { left: 30, right: 10, top: 10, bottom: 40 },
            frontierPoints: {
                radius: {
                    initial: 4,
                    enlarged: 6
                },
                stroke: {
                    initial: 1,
                    enlarged: 1.5
                }
            },
            skillsPoints: {
                radius: {
                    initial: 6,
                    enlarged: 9
                },
                stroke: {
                    initial: 1,
                    enlarged: 1.5
                }
            },
            axes: {
                fontSize: 13,
                labelToChartDistX: -15,
                labelToChartDistY: 15
            }
        },
        tooltip: {
            scatterPlot: {
                offsetX: 12,
                offsetY: 12
            },
            overTime: {
                width: 150,
                offsetX: 20,
                offsetY: 50
            }
        }
    },
    typography: {
        fontFamily: ['Poppins', 'sans-serif'].join(','),
        letterSpacing: '0rem',
        textAlign: 'left',
        inputFontSize: '0.875rem'
    }
});

/**
 * The `EBABtheme` is the main theme object used throughout the application.
 * It merges the base `BrandTheme` with specific component style overrides to create a comprehensive theme.
 * The overrides affect various MUI components, ensuring that the application has a consistent design.
 *
 * @constant
 * @type {Object}
 */
const EBABtheme = createTheme(BrandTheme, {
    typography: {
        header0: {
            fontWeight: 500,
            fontSize: '2.7rem'
        },
        header1: {
            fontWeight: 500,
            lineHeight: '2.2rem',
            fontSize: '2rem'
        },
        header2: {
            fontWeight: 500,
            lineHeight: '1.4rem',
            fontSize: '1.2rem'
        },
        h2: {
            fontSize: '1.2rem'
        },
        h3: {
            fontSize: '1.12rem'
        },
        h4: {
            fontSize: '1rem'
        },
        body: {
            fontWeight: 400,
            marginTop: '0.4rem',
            fontSize: '1rem'
        },
        body_show_medium_and_up: {
            fontWeight: 400,
            lineHeight: '1.3rem',
            marginTop: '0.4rem',
            fontSize: '1rem',
            [BrandTheme.breakpoints.down('sm')]: {
                display: 'none'
            }
        },
        body2: {
            fontWeight: 400,
            fontSize: '0.875rem'
        },
        footer: {
            color: BrandTheme.palette.orange.main,
            fontSize: '0.72rem'
        },
        zeroLine: {
            fill: 'none',
            opacity: 1,
            strokeWidth: 1.5,
            strokeDasharray: 5
        },
        smallprint: {
            fontSize: '0.75rem',
            fontWeight: 'lighter',
            color: BrandTheme.palette.disabled.darkest
        },
        headerMobile: {
            fontWeight: 500,
            lineHeight: '2.0rem',
            fontSize: '1.8rem',
            textAlign: 'center'
        }
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    lineHeight: '1rem',
                    borderRadius: 50,
                    textTransform: 'none',
                    fontSize: '1rem'
                },
                contained: {
                    backgroundColor: BrandTheme.palette.orange.main
                },
                outlined: {
                    color: BrandTheme.palette.orange.main,
                    borderColor: BrandTheme.palette.orange.main
                },
                text: {
                    color: BrandTheme.palette.common.white,
                    fontSize: '1rem',
                    fontWeight: 500,
                    height: 30
                },
                titleContained: {
                    fontSize: '1rem',
                    backgroundColor: BrandTheme.palette.orange.main,
                    color: BrandTheme.palette.common.white
                },
                titleOutlined: {
                    fontSize: '1rem',
                    color: BrandTheme.palette.orange.main,
                    borderColor: BrandTheme.palette.orange.main,
                    border: '2px solid'
                },
                minorContained: {
                    fontSize: '0.875rem',
                    backgroundColor: BrandTheme.palette.orange.main,
                    color: BrandTheme.palette.common.white,
                    height: 30
                },
                minorOutlined: {
                    fontSize: '0.875rem',
                    fontWeight: 500, // Medium
                    color: BrandTheme.palette.orange.main,
                    borderColor: BrandTheme.palette.orange.main,
                    border: '1.5px solid',
                    height: 30
                }
            }
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    fontSize: '0.64rem',
                    color: BrandTheme.palette.common.black,
                    backgroundColor: BrandTheme.palette.disabled.main,
                    placement: 'top-start',
                    textAlign: 'center',
                    maxWidth: 368
                }
            }
        },
        MuiDialog: {
            styleOverrides: {
                root: {},
                paper: {
                    [BrandTheme.breakpoints.up('sm')]: {
                        width: '450px',
                        height: '680px',
                        maxWidth: '30rem',
                        minWidth: '300px'
                    }
                },
                content: {
                    padding: BrandTheme.spacing(3),
                    paddingTop: BrandTheme.spacing(1),
                    paddingBottom: BrandTheme.spacing(2)
                },
                actions: {
                    padding: BrandTheme.spacing(1)
                }
            }
        },
        MuiTableCell: {
            styleOverrides: {
                head: {
                    backgroundColor: BrandTheme.palette.background.panel
                }
            }
        },
        MuiFilledInput: {
            styleOverrides: {
                root: {
                    color: BrandTheme.palette.disabled.darkest,
                    fontSize: BrandTheme.typography.inputFontSize,
                    height: '1.6rem',
                    borderRadius: 50
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    color: BrandTheme.palette.common.black,
                    fontSize: BrandTheme.typography.inputFontSize
                }
            }
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    color: BrandTheme.palette.common.black,
                    fontSize: BrandTheme.typography.inputFontSize
                }
            }
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontWeight: 400,
                    color: BrandTheme.palette.common.black,
                    fontSize: BrandTheme.typography.inputFontSize
                }
            }
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    fontWeight: 400,
                    lineHeight: '0.84rem',
                    color: BrandTheme.palette.common.black,
                    fontSize: BrandTheme.typography.inputFontSize
                }
            }
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    fontWeight: 400,
                    color: BrandTheme.palette.common.white,
                    fontSize: '0.72rem',
                    lineHeight: '1rem',
                    backgroundColor: BrandTheme.palette.kimberley.main
                }
            }
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    height: '4rem',
                    color: BrandTheme.palette.common.black,
                    backgroundColor: BrandTheme.palette.background.panel
                }
            }
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    fontSize: '0.67rem',
                    color: BrandTheme.palette.disabled.darkest
                }
            }
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    color: BrandTheme.palette.chathamsBlue.lighter,
                    textDecorationColor: BrandTheme.palette.chathamsBlue.lighter
                }
            }
        }
    }
});

export default EBABtheme;
